import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app';
import { Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, Firestore, initializeFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, Functions, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, FirebaseStorage, getStorage } from 'firebase/storage';

export interface JcFirebase {
    readonly app: FirebaseApp;
    readonly auth: Auth;
    readonly firestore: Firestore;
    readonly storage: FirebaseStorage;
    readonly functions: Functions;
}

export interface JcEmulatorPorts {
    readonly auth: number;
    readonly firestore: number;
    readonly storage: number;
    readonly functions: number;
}

const DEFAULT_EMULATOR_PORTS: JcEmulatorPorts = { auth: 9099, firestore: 8080, storage: 9199, functions: 5001 };

export interface JcFirebaseOptions {
    /** Connect to the Local Emulator Suite. */
    readonly useEmulators?: boolean;
    /** Emulator ports (must match the app's firebase.json); defaults to Firebase's. */
    readonly emulatorPorts?: Partial<JcEmulatorPorts>;
}

let instance: JcFirebase | null = null;

/**
 * Initializes the app's Firebase project. Call once at startup, before any
 * service touches Firebase; services resolve their instances lazily.
 */
export const initJcFirebase = (config: FirebaseOptions, options: JcFirebaseOptions = {}): JcFirebase => {
    if (instance !== null) {
        throw new Error('initJcFirebase called more than once');
    }
    const app = initializeApp(config);
    const auth = getAuth(app);
    // `ignoreUndefinedProperties` omits undefined fields instead of throwing.
    // `experimentalForceLongPolling` avoids the WebChannel transport, which hangs
    // ~30s on some mobile/proxy networks before falling back (firebase-js-sdk#8510).
    const firestore = initializeFirestore(app, {
        ignoreUndefinedProperties: true,
        experimentalForceLongPolling: true,
    });
    const storage = getStorage(app);
    const functions = getFunctions(app);

    if (options.useEmulators) {
        const ports = { ...DEFAULT_EMULATOR_PORTS, ...options.emulatorPorts };
        connectAuthEmulator(auth, `http://localhost:${ports.auth}`, { disableWarnings: true });
        connectFirestoreEmulator(firestore, 'localhost', ports.firestore);
        connectStorageEmulator(storage, 'localhost', ports.storage);
        connectFunctionsEmulator(functions, 'localhost', ports.functions);
        console.info(`[firebase] Connected to local emulators (auth:${ports.auth}, firestore:${ports.firestore}, storage:${ports.storage}, functions:${ports.functions})`);
    }

    instance = { app, auth, firestore, storage, functions };
    return instance;
};

const jcFirebase = (): JcFirebase => {
    if (instance === null) {
        throw new Error('Firebase used before initJcFirebase');
    }
    return instance;
};

export const jcAuth = (): Auth => jcFirebase().auth;
export const jcFirestore = (): Firestore => jcFirebase().firestore;
export const jcStorage = (): FirebaseStorage => jcFirebase().storage;
